import React, { useRef } from 'react'

import { ParentSize } from '@visx/responsive'

import { scaleLinear } from '@visx/scale'

import { Box, createStyles, makeAppStyles, RoundedPlainTextButton, useAppTheme } from '@percept/mui'

import * as Flags from '@percept/mui/flags'

import { CloudDownload } from '@percept/mui/icons'

import { downloadSVG } from '@percept/utils'

import { vodafoneMarketsById } from './lib'

import { clamp } from 'lodash-es'

import { ReportProviderV2 } from '@percept/types'


export type BenchmarkDatum = {
  org_unit_id: string
  provider: ReportProviderV2
  segment: string
  color: string
  effectiveness: number | null
  outcomes: number | null
}

type DatumRendererProps = { datum: BenchmarkDatum } & Record<'size' | 'x' | 'y', number>

type DatumRenderer = (props: DatumRendererProps) => React.ReactElement | null

export type BenchmarkingQuadrantChartSVGProps = (
  {
    benchmarkData: BenchmarkDatum[]
    datumRenderer?: DatumRenderer
    getTooltipContent?: ((datum: BenchmarkDatum) => string) | null
    costEffectivenessMetric?: string | null
    width: number
    axisMargin?: number
    axisTickFontSize?: number
    axisLabelFontSize?: number
    markerSize?: number
    markerPadding?: number
    svgRef?: React.MutableRefObject<SVGSVGElement | null>
  } & (
    {
      showLegend?: never
      getBenchmarkDatumLabel?: never
    } | {
      showLegend: boolean
      getBenchmarkDatumLabel: (datum: BenchmarkDatum) => string
    }
  )
)

export const MarketDatumRenderer = ({ datum, size, x, y }: DatumRendererProps): React.ReactElement | null => {
  const vodafoneMarket = vodafoneMarketsById[datum.org_unit_id]
  if( !vodafoneMarket ) return null
  const FlagIcon = Flags[vodafoneMarket.iso_code as Flags.FlagName]
  if( !FlagIcon ) return null
  return (
    <FlagIcon
      rounded
      border='white'
      size={size}
      x={x}
      y={y} />
  )
}

export const DefaultDatumRenderer = ({ datum, size, x, y }: DatumRendererProps): React.ReactElement => {
  const halfSize = size / 2
  x += halfSize
  y += halfSize
  return (
    <g>
      <circle
        cx={x}
        cy={y}
        r={halfSize}
        fill={datum.color} />
      <circle
        cx={x}
        cy={y}
        r={halfSize}
        stroke='white'
        strokeWidth={1}
        fill='none' />
    </g>
  )
}

export const getMarketTooltipContent = (datum: BenchmarkDatum): string => {
  const vodafoneMarket = vodafoneMarketsById[datum.org_unit_id]
  if( !vodafoneMarket ) return ''
  return vodafoneMarket.iso_code
}

export const getChannelTooltipContent = (datum: BenchmarkDatum): string => (
  `${datum.provider} - ${datum.segment}`
)

export type BenchmarkingQuadrantChartProps = Omit<BenchmarkingQuadrantChartSVGProps, 'width'>

// Global CSS selector for tooltips, so we can rely on a stable
// identifier in order to remove these from exported SVGs
const TOOLTIP_CSS_CLASSNAME = 'benchmarkQuadrantChart-tooltip'
const TOOLTIP_CSS_SELECTOR = `.${TOOLTIP_CSS_CLASSNAME}`

const useStyles = makeAppStyles( theme => ({
  tooltipContainer: {
    [`&:hover > ${TOOLTIP_CSS_SELECTOR}`]: {
      opacity: 1,
    }
  },
  // NOTE - createStyles is required for global styles
  ...createStyles({
    '@global': {
      [TOOLTIP_CSS_SELECTOR]: {
        opacity: 0,
        transition: theme.transitions.create('opacity'),
        userSelect: 'none',  
      }
    },
  }),
}))


const BenchmarkingChartLegend = ({
  benchmarkData,
  itemHeight,
  itemFontSize,
  markerSize,
  width,
  getBenchmarkDatumLabel,
  ...props
}: {
  benchmarkData: BenchmarkDatum[]
  itemHeight: number
  itemFontSize: number
  markerSize: number
  width: number
  getBenchmarkDatumLabel: (datum: BenchmarkDatum) => string
} & Omit<React.SVGAttributes<SVGSVGElement>, 'height'>): JSX.Element => {
  const halfItemHeight = itemHeight / 2
  const halfMarkerSize = markerSize / 2
  const height = benchmarkData.length * itemHeight
  return (
    <svg
      height={height}
      width={width}
      {...props}>
      { benchmarkData.map( (d, i) => (
        <g
          key={i}
          transform={`translate(0 ${i * itemHeight})`}>
          <circle
            fill={d.color}
            cx={halfMarkerSize}
            cy={halfItemHeight}
            r={halfMarkerSize} />
          <text
            x={markerSize + 4}
            y={halfItemHeight}
            dominantBaseline='central'
            fontSize={itemFontSize}>
            { getBenchmarkDatumLabel(d) }
          </text>
        </g>
      ))}
    </svg>
  )
}


const BenchmarkingQuadrantChartSVG = ({
  benchmarkData,
  datumRenderer = DefaultDatumRenderer,
  getTooltipContent,
  costEffectivenessMetric,
  width,
  axisMargin = 30,
  axisLabelFontSize = 14,
  axisTickFontSize = 12,
  markerSize = 16,
  markerPadding = 2,
  svgRef,
  showLegend,
  getBenchmarkDatumLabel,
}: BenchmarkingQuadrantChartSVGProps): JSX.Element => {
  const size = width - axisMargin
  const quadrantSize = size / 2

  const legendItemHeight = 22
  const legendItemFontSize = 12
  const legendMarkerSize = 10

  const height = (
    showLegend ?
      width + (benchmarkData.length * legendItemHeight) :
      width
  )

  const appTheme = useAppTheme()
  const colourTopLeft = appTheme.chart.healthColourScale(0.75)
  const colourTopRight = appTheme.chart.healthColourScale(1)
  const colourBottomLeft = appTheme.chart.healthColourScale(0)
  const colourBottomRight = appTheme.chart.healthColourScale(0.5)

  const markerRadius = markerSize / 2

  const quadrantScale = scaleLinear({
    domain: [-1, 1],
    range: [
      markerRadius + markerPadding,
      size - markerRadius - markerPadding,
    ],
  })

  const classes = useStyles()

  const costEffectivenessLabel = (
    costEffectivenessMetric ? 
      `Cost Effectiveness (${costEffectivenessMetric})` :
      'Cost Effectiveness'
  )

  return (
    <svg ref={svgRef} width={width} height={height}>
      {/* Background quadrants */}
      <g>
        <rect
          x={axisMargin}
          y={0}
          width={quadrantSize}
          height={quadrantSize}
          fill={colourTopLeft} />
        <rect
          x={axisMargin + quadrantSize}
          y={0}
          width={quadrantSize}
          height={quadrantSize}
          fill={colourTopRight} />
        <rect
          x={axisMargin}
          y={quadrantSize}
          width={quadrantSize}
          height={quadrantSize}
          fill={colourBottomLeft} />
        <rect
          x={axisMargin + quadrantSize}
          y={quadrantSize}
          width={quadrantSize}
          height={quadrantSize}
          fill={colourBottomRight} />
      </g>

      {/* Axis marks */}
      <g>
        <text
          x={axisMargin}
          dx={-4}
          y={0}
          fontSize={axisTickFontSize}
          dominantBaseline='middle'
          textAnchor='end'>
          +100%
        </text>
        <text
          x={axisMargin}
          dx={-2}
          y={size}
          dy={6}
          fontSize={axisTickFontSize}
          dominantBaseline='hanging'
          textAnchor='end'>
          -100%
        </text>
        <text
          x={width}
          y={size}
          dy={6}
          fontSize={axisTickFontSize}
          dominantBaseline='hanging'
          textAnchor='middle'>
          +100%
        </text>
      </g>

      {/* Axis Labels */}
      <g>
        <text
          x={axisMargin / 4}
          y={quadrantSize}
          fontSize={axisLabelFontSize}
          dominantBaseline='hanging'
          transform={`rotate(-90 ${axisMargin / 4} ${quadrantSize})`}
          textAnchor='middle'>
          Business Outcomes
        </text>
        <text
          x={axisMargin + quadrantSize}
          y={size}
          dy={10}
          fontSize={axisLabelFontSize}
          dominantBaseline='hanging'
          textAnchor='middle'>
          {costEffectivenessLabel}
        </text>
      </g>

      {/* <g className='midpoint-grid'>
        <line
          x1={axisMargin}
          y1={quadrantSize / 2}
          x2={size + axisMargin}
          y2={quadrantSize / 2}
          stroke='black'
        />
        <line
          x1={axisMargin}
          y1={size - quadrantSize / 2}
          x2={size + axisMargin}
          y2={size - quadrantSize / 2}
          stroke='black'
        />
        
        <line
          x1={axisMargin + quadrantSize / 2}
          y1={0}
          x2={axisMargin + quadrantSize / 2}
          y2={size}
          stroke='black'
        />
        <line
          x1={size - quadrantSize / 2}
          y1={0}
          x2={size - quadrantSize / 2}
          y2={size}
          stroke='black'
        />
      </g> */}

      <svg
        x={axisMargin}
        y={0}
        width={size}
        height={size}>
        { benchmarkData.map( d => {
          // const vodafoneMarket = vodafoneMarketsById[d.org_unit_id]
          // if( !vodafoneMarket ) return null
          if( d.effectiveness === null || d.outcomes === null ) return null
          const x = quadrantScale(clamp(d.effectiveness, -1, 1))
          const y = size - quadrantScale(clamp(d.outcomes, -1, 1))
          const element = datumRenderer({
            datum: d,
            size: markerSize,
            x: x - markerRadius,
            y: y - markerRadius,
          })
          if( !element ){
            return null
          }
          const tooltipWidth = Math.max(markerRadius * 1.25, 20)
          const tooltipHeight = Math.max(tooltipWidth / 1.5, 16)
          return (
            <g key={d.org_unit_id} className={classes.tooltipContainer}>
              { element }
              { getTooltipContent && (
                <g className={TOOLTIP_CSS_CLASSNAME}>
                  <rect
                    fill='white'
                    stroke='black'
                    rx={4}
                    x={x - (tooltipWidth / 2)}
                    y={y - (tooltipHeight / 2)}
                    width={tooltipWidth}
                    height={tooltipHeight} />
                  <text
                    x={x}
                    y={y}
                    fontSize={tooltipHeight * 0.75}
                    fontWeight={700}
                    dominantBaseline='central'
                    textAnchor='middle'
                    fill='black'>
                    { getTooltipContent(d) }
                  </text>
                </g>
              )}
            </g>
          )
        })}
      </svg>

      { showLegend && (
        <BenchmarkingChartLegend
          itemHeight={legendItemHeight}
          itemFontSize={legendItemFontSize}
          markerSize={legendMarkerSize}
          width={size}
          x={axisMargin}
          y={size + axisMargin}
          benchmarkData={benchmarkData}
          getBenchmarkDatumLabel={getBenchmarkDatumLabel} />
      )}

    </svg>
  )
}

export const BenchmarkingQuadrantChart = (props: BenchmarkingQuadrantChartProps): JSX.Element => {
  return (
    <ParentSize>
      { (sizeProps): JSX.Element => (
        <BenchmarkingQuadrantChartSVG {...props} {...sizeProps} />
      )}
    </ParentSize>
  )
}


const downloadBenchmarkSVG = (
  element: SVGSVGElement,
  fileName: string,
): Promise<void> => {
  return downloadSVG(
    element,
    fileName,
    {
      wrappingElement: {
        margin: {
          top: 20,
          left: 20,
          bottom: 20,
          right: 20,
        },
        attributes: {
          overflow: 'visible',
        },
      },
      elementFilter: (el) => {
        const classList: string[] = Array.prototype.slice.call(el.classList)
        return !classList.includes(TOOLTIP_CSS_CLASSNAME)
      },
      elementModifier: (el) => {
        el.setAttribute('overflow', 'visible')
      }
    },
  )
}


export const ExportableBenchmarkQuadrantChart = ({
  title,
  downloadName,
  size,
  ...props
}: BenchmarkingQuadrantChartProps & {
    title: React.ReactNode
    downloadName: string
    size?: number
  }
): JSX.Element => {
  const svgRef = useRef<SVGSVGElement | null>(null)
  let chartContent = null
  if( size ){
    chartContent = (
      <BenchmarkingQuadrantChartSVG
        svgRef={svgRef}
        width={size}
        {...props} />
    )
  }else{
    chartContent = (
      <BenchmarkingQuadrantChart
        svgRef={svgRef}
        {...props} />
    )
  }
  return (
    <div>
      <Box
        display='flex'
        alignItems='center'
        justifyContent='space-between'
        mb={2}>
        { title }
        <RoundedPlainTextButton
          size='small'
          variant='contained'
          startIcon={<CloudDownload />}
          onClick={(): void => {
            if( svgRef.current ){
              downloadBenchmarkSVG(
                svgRef.current, `${downloadName}.svg`
              ).then(console.log)
            }
          }}>
          Download
        </RoundedPlainTextButton>
      </Box>
      { chartContent }
    </div>
  )
}
